import React, { useState, useRef, useEffect } from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { ArrowDownSLineIconV7 } from './figma-icons-v7';

interface UniversalDropdownProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | null;
  openUpward?: boolean;
}

export const UniversalDropdown: React.FC<UniversalDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  error,
  openUpward = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const colors = useBrandingColors();

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (newValue: string | number) => {
    onChange(newValue);
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    const option = options.find(opt => opt.value === value);
    if (option) {
      return option.label;
    }
    if (placeholder) {
      return placeholder;
    }
    return options[0]?.label || '';
  };

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef} data-dropdown="universal">
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`bg-[#ffffff] box-border content-stretch flex flex-row gap-2 items-center justify-center overflow-clip h-[42px] px-3 relative rounded-2xl shrink-0 w-full hover:bg-[#f6f8fa] transition-colors border focus:outline-none ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${error ? 'border-red-500' : ''}`}
        style={{ borderColor: error ? 'red' : '#cdd0d5' }}
        onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = '#cdd0d5'; }}
      >
        <div className="font-['Inter:Medium',_sans-serif] font-medium text-[#525866] text-[16px] tracking-[-0.176px] flex-grow text-left truncate pr-2">
          {getDisplayValue()}
        </div>
        
        <ArrowDownSLineIconV7 size={24} className={`w-6 h-6 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <div className={`absolute left-0 w-full bg-[#ffffff] rounded-2xl border border-[#cdd0d5] shadow-lg z-[9999] p-2 ${
          openUpward ? 'bottom-full mb-2' : 'top-full mt-2'
        }`}>
          <div className="flex flex-col gap-1 max-h-80 overflow-y-auto pr-1 show-scrollbar">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl font-['Inter:Medium',_sans-serif] font-medium text-[16px] transition-colors hover:bg-[#f6f8fa] text-[#525866] text-left"
                style={{ color: '#525866' }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {error && (
        <div className="text-red-500 text-[12px] font-['Inter:Regular',_sans-serif] mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

// Компонент dropdown с лейблом
interface UniversalDropdownWithLabelProps extends UniversalDropdownProps {
  label: string;
}

export const UniversalDropdownWithLabel: React.FC<UniversalDropdownWithLabelProps> = ({
  label,
  ...dropdownProps
}) => {
  return (
    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
      <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
        <p className="block leading-[20px]">{label}</p>
      </label>
      <UniversalDropdown {...dropdownProps} />
    </div>
  );
};
